import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Loader2, Link as LinkIcon, FileJson, User2 } from 'lucide-react';
import { useContactStore } from '@/stores/contactStore.js';
import { useAuth } from '@/contexts/AuthContext.js';
import { getApiUrl } from '@/lib/api-url.js';
import type { AgentCard } from '@/types/contact.js';

interface AddContactDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type InputMode = 'userId' | 'url' | 'json';

export function AddContactDialog({ open, onOpenChange }: AddContactDialogProps) {
    const [inputMode, setInputMode] = useState<InputMode>('userId');
    const [userId, setUserId] = useState('');
    const [url, setUrl] = useState('');
    const [jsonInput, setJsonInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { authenticatedFetch } = useAuth();
    const addContact = useContactStore((state) => state.addContact);

    const handleFetchByUserId = async () => {
        if (!userId) {
            setError('Please enter a User ID');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Fetch agent card using internal user endpoint
            const baseUrl = getApiUrl();
            const response = await authenticatedFetch(`${baseUrl}/users/${userId.trim()}/agent-card`);

            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.statusText}`);
            }

            const agentCard: AgentCard = await response.json();

            addContact(agentCard);
            onOpenChange(false);
            resetForm();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch agent card');
        } finally {
            setLoading(false);
        }
    };

    const handleFetchFromUrl = async () => {
        if (!url) {
            setError('Please enter a URL');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Construct .well-known URL
            const baseUrl = url.trim();
            const agentCardUrl = new URL('/.well-known/agent-card.json', baseUrl);

            const response = await fetch(agentCardUrl.toString());
            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.statusText}`);
            }

            const agentCard: AgentCard = await response.json();

            // TODO: Validate with Zod schema
            // const validated = AgentCardSchema.parse(agentCard);

            addContact(agentCard);
            onOpenChange(false);
            resetForm();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch agent card');
        } finally {
            setLoading(false);
        }
    };

    const handleAddFromJson = () => {
        if (!jsonInput) {
            setError('Please enter AgentCard JSON');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const agentCard: AgentCard = JSON.parse(jsonInput);

            // TODO: Validate with Zod schema
            // const validated = AgentCardSchema.parse(agentCard);

            addContact(agentCard);
            onOpenChange(false);
            resetForm();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid JSON');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setUserId('');
        setUrl('');
        setJsonInput('');
        setError('');
        setInputMode('userId');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputMode === 'userId') {
            handleFetchByUserId();
        } else if (inputMode === 'url') {
            handleFetchFromUrl();
        } else {
            handleAddFromJson();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Add Contact</DialogTitle>
                    <DialogDescription>
                        Add an agent to your contacts using their URL or AgentCard JSON.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    {/* Mode Toggle */}
                    <div className="flex gap-2 mb-4">
                        <Button
                            type="button"
                            variant={inputMode === 'userId' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setInputMode('userId')}
                            className="flex-1 gap-2"
                        >
                            <User2 className="h-4 w-4" />
                            User ID
                        </Button>
                        <Button
                            type="button"
                            variant={inputMode === 'url' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setInputMode('url')}
                            className="flex-1 gap-2"
                        >
                            <LinkIcon className="h-4 w-4" />
                            URL
                        </Button>
                        <Button
                            type="button"
                            variant={inputMode === 'json' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setInputMode('json')}
                            className="flex-1 gap-2"
                        >
                            <FileJson className="h-4 w-4" />
                            JSON
                        </Button>
                    </div>

                    {/* User ID Input Mode */}
                    {inputMode === 'userId' && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="userId" className="text-sm font-medium">
                                    User ID
                                </label>
                                <Input
                                    id="userId"
                                    placeholder="user-abc-123"
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    disabled={loading}
                                />
                                <p className="text-xs text-muted-foreground">
                                    For internal contacts on the same server instance
                                </p>
                            </div>
                        </div>
                    )}

                    {/* URL Input Mode */}
                    {inputMode === 'url' && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="url" className="text-sm font-medium">
                                    Agent URL
                                </label>
                                <Input
                                    id="url"
                                    placeholder="https://example.com"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    disabled={loading}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Will fetch from /.well-known/agent-card.json
                                </p>
                            </div>
                        </div>
                    )}

                    {/* JSON Input Mode */}
                    {inputMode === 'json' && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="json" className="text-sm font-medium">
                                    AgentCard JSON
                                </label>
                                <Textarea
                                    id="json"
                                    placeholder='{"name": "Agent Name", "description": "...", ...}'
                                    value={jsonInput}
                                    onChange={(e) => setJsonInput(e.target.value)}
                                    disabled={loading}
                                    className="min-h-[200px] font-mono text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="mt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Add Contact
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
